// Pure, DOM-free helpers for keeping a multi-file upload BOUND to the project it started in.
//
// WHY THIS EXISTS (real bug): the upload queue lives in an app-level provider so it survives
// navigating away from the File Upload page (see UploadQueueContext.jsx). That also means it
// survives SWITCHING PROJECTS mid-upload. Two things then go wrong unless the batch's project is
// captured once and honored everywhere:
//   1. DATA MISROUTE (the serious one): each file's X-Project-Id was resolved lazily from a
//      module var at request time, so files still queued when the user switched project were
//      uploaded/imported into the NEW project — not the one they were dropped into.
//   2. DISPLAY: the queue kept rendering under whatever project was now selected, implying files
//      were going somewhere they weren't.
// Both are fixed by capturing the project ONCE at batch start (batchOwnerProjectId) and using it
// for (1) every network call in the pipeline and (2) the decision of whether to show the queue.
// These two helpers are the pure core of that decision, extracted so they can be unit-tested
// without rendering the page (which pulls in Amplify/dropzone/XLSX). The page and the API layer
// call the same captured id, so what you SEE and where the data GOES can never diverge.

/**
 * Which project a file being uploaded must be pinned to.
 * @param {string|null} batchOwnerProjectId  project captured when the batch started (null for a
 *   single-file drop, which pins to the currently-active project instead).
 * @param {string|null} activeProjectId       the currently-selected project.
 * @returns {string|null} the project id to stamp on this file's requests.
 */
export function uploadTargetProjectId(batchOwnerProjectId, activeProjectId) {
  // A batch pins to its captured owner; a single-file drop (no owner) pins to the active project.
  return batchOwnerProjectId != null ? batchOwnerProjectId : activeProjectId;
}

/**
 * Whether the upload queue should be VISIBLE on the File Upload page right now.
 * @param {Array|null} batchQueue            the queue (null = no batch).
 * @param {string|null} batchOwnerProjectId  project the batch is uploading into.
 * @param {string|null} activeProjectId       the currently-selected project.
 * @returns {boolean}
 */
export function shouldShowBatch(batchQueue, batchOwnerProjectId, activeProjectId) {
  if (!batchQueue) return false;
  // No owner recorded (null) → show it. This preserves the pre-fix always-show behavior for any
  // batch that started before an owner was tracked, so we NEVER hide a genuine in-flight upload.
  if (batchOwnerProjectId == null) return true;
  return batchOwnerProjectId === activeProjectId;
}
